import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { CompetitionService } from '../../service/CompetitionService';
import LanguageService, { translatedMessage } from '../../service/LanguageService';
import { Dropdown } from 'primereact/dropdown';
import RecruitmentUtils from '../../utilities/RecruitmentUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import SunEditor from 'suneditor-react';
import GeneralUtils from '../../utilities/GeneralUtils';
import ApiService from '../../service/ApiService';
import { QuestionType } from './QuestionType';
import { InputNumber } from 'primereact/inputnumber';

let emptyAnswer = {
    id: null,
    subjectQuestion: null,
    answerContent: '',
    correctAnswer: false,
    correctOrder: null,
    secondContent: ''
};

const AnswerEditDialog = (props) => {
    const [answer, setAnswer] = useState(emptyAnswer);
    const [isNew, setIsNew] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const toast = useRef(null);
    const dialogToast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _answer = props.value ? props.value : emptyAnswer;
        setAnswer(_answer);
        setIsNew(!props.value?.id);
    }, [props]);

    useEffect(() => {
        setDialogVisible(props.visible);
    }, [props.visible]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: answer,
        validate: (data) => {
            let errors = {};

            if (!data.answerContent) {
                errors.answerContent = translatedMessage('form.error.answerContent.required');
            }
            if ([QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType)) {
                if (!data.secondContent) {
                    console.log('data.secondContent', !data.secondContent);
                    errors.secondContent = translatedMessage('form.error.secondContent.required');
                }
            }
            if ([QuestionType.ORDERING_ELEMENTS, QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType)) {
                if (!data.correctOrder) {
                    errors.correctOrder = translatedMessage('form.error.correctOrder.required');
                }
            }
            return errors;
        },
        onSubmit: (data) => {
            if (saveAnswer(data)) {
                formik.resetForm();
            }
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const hideAnswerDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        setDialogVisible(false);
        formik.resetForm();
    };

    const refreshParent = () => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(answer, false);
        }
    };

    const saveAnswer = (data) => {
        data.subjectQuestion = { ...props.question, answers: null };
        competitionService
            .saveQuestionAnswer(data)
            .then((savedAnswer) => {
                dialogToast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                hideAnswerDialog();
                refreshParent();
                return true;
            })
            .catch((error) => {
                dialogToast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
                return false;
            });
    };

    const answerDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideAnswerDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="answer-form" type="submit" />
        </>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={dialogVisible} style={{ width: '850px' }} header={translatedMessage('competition.subjectProposal.question.answer.details')} modal className="p-fluid" footer={answerDialogFooter} onHide={hideAnswerDialog}>
                <Toast ref={dialogToast} />
                <form id="answer-form" onSubmit={formik.handleSubmit}>
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <div className="pcn-sun-editor w-100 mb-0">
                                        <label htmlFor="answerContent" className={classNames({ 'p-error': isFormFieldValid('answerContent') })}>
                                            {translatedMessage('competition.subjectProposal.question.answer')}
                                        </label>
                                        <SunEditor
                                            id="answerContent"
                                            name="answerContent"
                                            lang={LanguageService.getCurrentLanguage}
                                            height="200px"
                                            width="auto"
                                            defaultValue={formik.values.answerContent}
                                            onValueChange={formik.handleChange}
                                            placeholder={''}
                                            setOptions={{
                                                buttonList: GeneralUtils.getSunEditorButtonsWithImage(),
                                                font: GeneralUtils.getSunEditorFonts(),
                                                defaultStyle: GeneralUtils.getSunEditorDefaultStyle(),
                                                charCounter: ApiService.getIsProduction() ? true : false,
                                                maxCharCount: ApiService.getIsProduction() ? 10000 : null,
                                                imageUploadSizeLimit: 5000000,
                                                imageAccept: '.jpg,.jpeg,.png',
                                                imageResizing: true,
                                                imageHeightShow: true,
                                                imageWidth: 'auto',
                                                imageRotation: false,
                                                imageFileInput: true,
                                                imageUrlInput: false,
                                                imageTabDisable: true
                                                /*                                plugins: [math],
                                                                katex: katex*/
                                            }}
                                            onChange={(e) => formik.setFieldValue('answerContent', e)}
                                        />

                                        {getFormErrorMessage('answerContent')}
                                    </div>
                                </td>
                                {[QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType) && (
                                    <td style={{ width: '50%' }}>
                                        <div className="pcn-sun-editor w-100 mb-0">
                                            <label htmlFor="secondContent" className={classNames({ 'p-error': isFormFieldValid('secondContent') })}>
                                                {translatedMessage('competition.subjectProposal.question.answer.secondContent')}
                                            </label>
                                            <SunEditor
                                                id="secondContent"
                                                name="secondContent"
                                                lang={LanguageService.getCurrentLanguage}
                                                height="200px"
                                                width="auto"
                                                defaultValue={formik.values.secondContent}
                                                onValueChange={formik.handleChange}
                                                placeholder={''}
                                                setOptions={{
                                                    buttonList: GeneralUtils.getSunEditorButtonsWithImage(),
                                                    font: GeneralUtils.getSunEditorFonts(),
                                                    defaultStyle: GeneralUtils.getSunEditorDefaultStyle(),
                                                    charCounter: ApiService.getIsProduction() ? true : false,
                                                    maxCharCount: ApiService.getIsProduction() ? 10000 : null,
                                                    imageUploadSizeLimit: 5000000,
                                                    imageAccept: '.jpg,.jpeg,.png',
                                                    imageResizing: true,
                                                    imageHeightShow: true,
                                                    imageWidth: 'auto',
                                                    imageRotation: false,
                                                    imageFileInput: true,
                                                    imageUrlInput: false,
                                                    imageTabDisable: true
                                                    /*                                plugins: [math],
                                                                katex: katex*/
                                                }}
                                                onChange={(e) => formik.setFieldValue('secondContent', e)}
                                            />

                                            {getFormErrorMessage('secondContent')}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        </tbody>
                    </table>

                    {[QuestionType.SINGLE_SELECTION, QuestionType.MULTIPLE_SELECTION].includes(props.question.questionType) && (
                        <div className="p-inputgroup mt-3">
                            <span className="p-float-label">
                                <Dropdown
                                    id="correctAnswer"
                                    name="correctAnswer"
                                    value={formik.values.correctAnswer}
                                    options={RecruitmentUtils.yesNoDropdownOptions()}
                                    onChange={(e) => formik.setFieldValue('correctAnswer', e.value)}
                                    itemTemplate={(e) => DataTableUtils.yesNoTemplate(e.value)}
                                />

                                <label htmlFor="correctAnswer" className={classNames({ 'p-error': isFormFieldValid('correctAnswer') })}>
                                    {translatedMessage('competition.subjectProposal.question.answer.correct')}
                                </label>
                            </span>
                        </div>
                    )}

                    {[QuestionType.ORDERING_ELEMENTS, QuestionType.MATCHING_ELEMENTS].includes(props.question.questionType) && (
                        <>
                            <div className="p-inputgroup mt-3">
                                <span className="p-float-label">
                                    <InputNumber
                                        id="correctOrder"
                                        name="correctOrder"
                                        value={formik.values.correctOrder}
                                        onValueChange={formik.handleChange}
                                        min={0}
                                        max={100}
                                        className={classNames({ 'p-invalid': isFormFieldValid('correctOrder') })}
                                        useGrouping={false}
                                        maxFractionDigits={2}
                                    />
                                    <label htmlFor="correctOrder" className={classNames({ 'p-error': isFormFieldValid('correctOrder') })}>
                                        {translatedMessage('competition.subjectProposal.question.answer.correctOrder')}*
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('correctOrder')}
                        </>
                    )}
                </form>
            </Dialog>
        </>
    );
};

export default AnswerEditDialog;
